import useAuth from '@app/auth/use-auth';
import ReportContent from '@app/components/reports/components/report-content';
import ReportMetric from '@app/components/reports/components/report-metric';
import { totalEmissionsAxisProps } from '@app/components/reports/config';
import { ReportSectionComponent } from '@app/components/reports/types';
import {
  useGetReportSectionData,
  useGetReportTypeCopy,
} from '@app/components/reports/utils';
import { useAccount } from '@shared/api';
import { EmissionsCategoryEnum } from '@shared/api/types';
import SimpleBarChart from '@shared/components/chart/simple-bar-chart';
import Text from '@shared/components/content/text';
import RatingCloud from '@shared/components/ratings/rating-cloud';
import { ChartConfig } from '@shared/components/ui/chart';
import { getCurrencySymbol } from '@shared/lib/format';
import { capitalizeFirstLetter } from '@shared/lib/utils';

interface FoodAndDrinkProps extends ReportSectionComponent {}

export default function FoodAndDrink({
  reportType,
  downloadMode,
}: FoodAndDrinkProps) {
  const {
    result: { foodAndDrink },
  } = useGetReportSectionData(reportType);
  const reportTypeCopy = useGetReportTypeCopy(reportType);
  const { user } = useAuth();
  const hasCostFeature = !!user?.features.cost;
  const { data: account } = useAccount(true);
  const totalSpend =
    foodAndDrink?.totalSpend != null ? Number(foodAndDrink.totalSpend) : null;

  const chartData = foodAndDrink?.emissionsByLifecycleStage.map((item) => ({
    stage: capitalizeFirstLetter(item.stage),
    emissions: item.totalEmissionsTonnes,
  }));

  const emissionsDataMax = Math.max(
    0,
    ...(chartData?.map((d) => d.emissions) ?? [])
  );

  const chartConfig = {
    emissions: {
      label: 'Emissions',
      color: 'var(--chart-2)',
      unit: 'tCO2e',
    },
  } satisfies ChartConfig;

  const data = {
    totalFoodEmissions: foodAndDrink?.totalEmissionsTonnes,
    totalFoodPurchased: foodAndDrink?.totalWeightKg
      ? foodAndDrink.totalWeightKg / 1000
      : 0,
    overallCarbonIntensity: foodAndDrink?.averageCarbonIntensity,
    rating: foodAndDrink?.carbonIntensityRating as EmissionsCategoryEnum | '',
  };

  return (
    <ReportContent>
      <>
        <Text variant="reportBody">
          To calculate the total emissions from your {reportTypeCopy.past} food
          and drink, each item you {reportTypeCopy.verb} is matched to a
          standard item in our database, with a corresponding emission factor.
          The overall emissions for each item is the emission factor multiplied
          by the weight {reportTypeCopy.past}.
        </Text>
        <ReportMetric
          title="Total food emissions"
          value={data.totalFoodEmissions ?? 0}
          unit="tCO2e"
          size="lg"
          className="flex-1"
        />

        <div className="flex space-x-2">
          <ReportMetric
            title={`Total food ${reportTypeCopy.past}`}
            value={data.totalFoodPurchased ?? 0}
            unit="tonnes"
            size="md"
            className="flex-1"
          />
          <ReportMetric
            title="Overall carbon intensity"
            value={data.overallCarbonIntensity ?? 0}
            unit="kgCO2e/kg"
            size="md"
            leftAdornment={
              <RatingCloud
                rating={data.rating as EmissionsCategoryEnum}
                format={downloadMode ? 'png' : 'svg'}
              />
            }
            className="flex-1"
          />
        </div>
        {hasCostFeature && totalSpend != null && (
          <ReportMetric
            title="Total food spend"
            value={totalSpend}
            unit={getCurrencySymbol(account?.defaultCurrency)}
            size="md"
            unitClassName="text-4xl text-gray-400 font-medium order-first"
          />
        )}
      </>
      <>
        <Text variant="h3">Emissions by life cycle stage</Text>
        <Text variant="reportBody">
          The emissions for each food item in our database can be broken down
          into 4 distinct stages: Farming, processing, packaging and transport.
          Combined, these represent the cradle-to-store emissions that occur
          before you receive the item.
          <br />
          <br />
          The chart below shows the overall emissions that occur in each of
          these stages, from your {reportTypeCopy.past} items.
        </Text>

        <SimpleBarChart
          nameKey="stage"
          dataKey="emissions"
          chartData={chartData}
          chartConfig={chartConfig}
          className="h-max max-h-[calc(var(--content-title-height)-20rem)] w-full flex-1"
          margin={{ top: 50 }}
          YAxisProps={totalEmissionsAxisProps(emissionsDataMax)}
        />
      </>
    </ReportContent>
  );
}
